import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import {
  FaHome,
  FaUsers,
  FaSeedling,
  FaShieldAlt,
  FaLeaf,
  FaIgloo,
  FaChurch,
  FaCubes,
  FaLayerGroup,
  FaThLarge,
  FaRegHeart,
} from "react-icons/fa";
import "./Housing.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const HERO_FEATURES = [
  { icon: FaHome, label: "Safe & Affordable" },
  { icon: FaUsers, label: "Dignified Living" },
  { icon: FaSeedling, label: "Sustainable Communities" },
  { icon: FaShieldAlt, label: "Stronger Tomorrow" },
];

const STATS = [
  { icon: FaHome, value: "1000+", label: "Homes Built" },
  { icon: FaUsers, value: "50+", label: "Communities Impacted" },
  { icon: FaSeedling, value: "20+", label: "Years of Experience" },
  { icon: FaShieldAlt, value: "", label: "Sustainable Housing Solutions" },
];

const TECHNOLOGIES = [
  {
    icon: FaIgloo,
    title: "Pabel Dome",
    text: "Cost-effective and disaster-resilient construction technology.",
  },
  {
    icon: FaChurch,
    title: "Gothic Model",
    text: "Architecturally inspired design for strength and durability.",
  },
  {
    icon: FaCubes,
    title: "Pre-cast Materials",
    text: "High-quality materials for faster and long-lasting construction.",
  },
  {
    icon: FaLayerGroup,
    title: "Ferro-cement",
    text: "Lightweight, durable and ideal for low-cost housing.",
  },
  {
    icon: FaThLarge,
    title: "Brick Dome",
    text: "Eco-friendly and energy-efficient housing solution.",
  },
  {
    icon: FaLeaf,
    title: "Mud Grass Roof",
    text: "Natural insulation for a cooler and sustainable home.",
  },
];

const Housing = () => {
  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* ================= Hero (1521 x 482, with feature pill bar) ================= */}
      <section className="hs-page">
        <div className="hs-container">
          <motion.div
            className="hs-hero"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            <div
              className="hs-hero__image"
              style={{ backgroundImage: "url(/assets/images/backgrounds/housing.png)" }}
            />
            <div className="hs-hero__overlay" />

            <div className="hs-hero__content">
              <div className="hs-hero__brand">
                <span className="hs-hero__badge" />
                <span className="hs-hero__brandLabel">Vikash Foundation</span>
              </div>

              <motion.h2
                className="hs-hero__title"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 }}
              >
                Housing
              </motion.h2>

              <span className="hs-hero__dash">
                <FaLeaf />
              </span>

              <motion.p
                className="hs-hero__text"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.3 }}
              >
                Vikash supports housing initiatives by providing safe and
                affordable housing solutions to enhance the living conditions
                of people in need.
              </motion.p>
            </div>

            <div className="hs-hero__pillBar">
              {HERO_FEATURES.map((f) => {
                const Icon = f.icon;
                return (
                  <div className="hs-hero__pillItem" key={f.label}>
                    <span className="hs-hero__pillIcon">
                      <Icon />
                    </span>
                    <span>{f.label}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= Introduction ================= */}
      <section className="hs-intro">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-start g-5">
              <motion.div className="col-lg-5" variants={staggerItem}>
                <span className="hs-eyebrow">
                  <FaLeaf /> Our Mission
                </span>
                <h2 className="hs-heading">
                  Building Resilient
                  <br />
                  Communities
                </h2>
                <span className="hs-heading__underline">
                  <FaLeaf />
                </span>
                <p className="hs-intro__text">
                  Vikash has undertaken extensive action research to promote
                  alternative and sustainable housing technologies. In the
                  wake of the Super Cyclone of 1999, we constructed
                  individual houses, community buildings, and cyclone
                  shelter-cum-school buildings in Puri district,
                  rehabilitating affected populations with innovative and
                  resilient housing models.
                </p>
              </motion.div>

              <motion.div className="col-lg-7" variants={staggerItem}>
                <img
                  src="/assets/images/sections/housing.jpg"
                  alt="Affordable housing project"
                  className="hs-intro__image"
                />
                <div className="hs-statsRow">
                  {STATS.map((s) => {
                    const Icon = s.icon;
                    return (
                      <div className="hs-statsRow__item" key={s.label}>
                        <span className="hs-statsRow__icon">
                          <Icon />
                        </span>
                        <div>
                          {s.value && <div className="hs-statsRow__value">{s.value}</div>}
                          <div className="hs-statsRow__label">{s.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= Technologies ================= */}
      <section className="hs-approach">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="hs-approach__header"
          >
            <span className="hs-eyebrow hs-eyebrow--center">
              <FaLeaf /> Our Alternative Housing Models & Technologies
            </span>
            <h2 className="hs-heading hs-heading--center">
              Innovative. Sustainable. Impactful.
            </h2>
            <span className="hs-heading__underline hs-heading__underline--center">
              <FaLeaf />
            </span>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {TECHNOLOGIES.map((tech) => {
                const Icon = tech.icon;
                return (
                  <Col md={6} lg={4} key={tech.title}>
                    <motion.div variants={staggerItem} className="hs-card">
                      <span className="hs-card__icon">
                        <Icon />
                      </span>
                      <h5 className="hs-card__title">{tech.title}</h5>
                      <p className="hs-card__text">{tech.text}</p>
                    </motion.div>
                  </Col>
                );
              })}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* ================= CTA ================= */}
      <section className="hs-cta">
        <Container>
          <motion.div
            className="hs-cta__card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div
              className="hs-cta__bg"
              style={{ backgroundImage: "url(/assets/images/sections/house-model.jpg)" }}
            />
            <div className="hs-cta__overlay" />

            <div className="hs-cta__content">
              <span className="hs-eyebrow hs-eyebrow--light">
                <FaLeaf /> Make A Difference
              </span>
              <h2 className="hs-cta__title">Help Build a Safe Home</h2>
              <span className="hs-heading__underline hs-heading__underline--light">
                <FaLeaf />
              </span>
              <p className="hs-cta__text">
                Your contribution helps us provide secure and sustainable
                housing for families living in vulnerable communities.
              </p>
              <div className="hs-cta__actions">
                <button
                  type="button"
                  className="hs-btn hs-btn--solid"
                  onClick={openDonation}
                >
                  <FaRegHeart /> Donate
                </button>
                <button
                  type="button"
                  className="hs-btn hs-btn--outline"
                  onClick={() => navigate("/interns")}
                >
                  <FaUsers /> Join Us
                </button>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default Housing;